// Admin · Widget Covers
//
// Batch editor for the property covers shown across every instance of
// the booking widget (home page + hotel-detail pages), covering ALL
// four surfaces where the cover appears:
//   1. `PropertyCarousel` — the visual "Browse the properties" strip
//   2. The "Choose a property" dropdown checklist
//   3. The per-property blocks below the dropdown
//   4. The itinerary strip (chronological summary)
//   5. `StickySummary` floating enquiry recap
//
// One slot per hotel — `booking-widget.thumb.<slug>` — mutated here
// propagates instantly to every surface listed above (they all read
// from the same slotCache subscription).
//
// UX intent:
//   • See all 12+ current covers at a glance (compact 4-col grid).
//   • Edit any single cover in-modal (reuses the standard
//     `ImageEditModal` so upload / URL paste / Pexels / Bunny library
//     all work identically to the CMS pencil).
//   • Reset one cover back to the hotel's canonical hero
//     (`hotel/<slug>/about-hotel`) with a single click.
//   • Live preview: the same `PropertyCarousel` used on the site is
//     rendered at the bottom so editors can eyeball their changes in
//     the exact context guests will see, without leaving the page.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RotateCcw, Pencil, Loader2, AlertCircle, CheckCircle2,
  ImagePlus, Eye, LayoutGrid,
} from "lucide-react";
import AdminAuthGate, { adminHeaders } from "@/components/admin/AdminAuthGate";
import { ImageEditModal } from "@/components/cms/CmsImage";
import { HOTELS } from "@/lib/data";
import { resetSlotCaches, resolveUrl } from "@/lib/slotCache";
import PropertyCarousel from "@/components/booking/PropertyCarousel";
import { useLanguage } from "@/contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL || "";

const pick = (dict, lang) => (typeof dict === "string" ? dict : (dict?.[lang] || dict?.en || ""));

const widgetSlotFor = (slug) => `booking-widget.thumb.${slug}`;
const heroSlotFor = (slug) => `hotel/${slug}/about-hotel`;

// Rendering state for a single cover cell.
const stateOf = (widget, hero) => {
  if (widget && widget.url && !widget.cleared) return "custom";
  if (hero && hero.url && !hero.cleared) return "hero";
  return "default";
};
const STATE_META = {
  custom:  { label: "WIDGET COVER SET",   className: "bg-[#C16542] text-[#FDFBF7]" },
  hero:    { label: "USING HOTEL HERO",   className: "bg-[#2C2621]/85 text-[#FDFBF7]" },
  default: { label: "BRAND DEFAULT",      className: "bg-[#E8DBC4] text-[#5C5248]" },
};

const CATEGORY_LABELS = {
  hotel:   "Hotel",
  bivouac: "Bivouac",
  refuge:  "Refuge",
};

const AdminWidgetCoversInner = () => {
  const { lang } = useLanguage();
  const [dbSlots, setDbSlots] = useState({});    // { slot_id → {url, cleared, ...} }
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(null);   // { slug, slot, currentUrl }
  const [resettingSlug, setResettingSlug] = useState(null);
  const [flash, setFlash] = useState(null);       // { type: 'ok' | 'err', msg }

  // We only need the covers for hotels that actually appear in the
  // widget — the widget hides `coming_soon` entries, so we match that.
  const hotels = useMemo(() => HOTELS.filter((h) => !h.coming_soon), []);

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await fetch(`${API}/api/slots`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const map = {};
      (data.slots || []).forEach((s) => { if (s && s.slot_id) map[s.slot_id] = s; });
      setDbSlots(map);
    } catch (err) {
      setLoadError(err.message || "Failed to load slots");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const rows = useMemo(() => hotels.map((h) => {
    const widgetSlot = widgetSlotFor(h.slug);
    const heroSlot = heroSlotFor(h.slug);
    const widget = dbSlots[widgetSlot];
    const hero = dbSlots[heroSlot];
    let displayUrl = null;
    if (widget && !widget.cleared && widget.url) displayUrl = resolveUrl(widget.url);
    else if (hero && !hero.cleared && hero.url) displayUrl = resolveUrl(hero.url);
    else displayUrl = h.image || null;
    return {
      slug: h.slug,
      name: h.name,
      category: h.category,
      location: pick(h.location, lang),
      widget,
      hero,
      widgetSlot,
      state: stateOf(widget, hero),
      displayUrl,
    };
  }), [hotels, dbSlots, lang]);

  const handleEdit = (row) => {
    setEditing({
      slug: row.slug,
      slot: row.widgetSlot,
      currentUrl: (row.widget && !row.widget.cleared) ? resolveUrl(row.widget.url) : "",
      currentAlt: "",
      currentAltI18n: { en: "", fr: "", es: "" },
    });
  };

  const handleEditClose = () => {
    setEditing(null);
    resetSlotCaches?.();
    fetchSlots();
  };

  const handleReset = async (row) => {
    setResettingSlug(row.slug);
    setFlash(null);
    try {
      const res = await fetch(
        `${API}/api/slots/${encodeURIComponent(row.widgetSlot)}`,
        { method: "DELETE", headers: adminHeaders() }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      resetSlotCaches?.();
      await fetchSlots();
      setFlash({ type: "ok", msg: `${row.name} reset — widget will fall back to hotel hero.` });
    } catch (err) {
      setFlash({ type: "err", msg: err.message || "Reset failed" });
    } finally {
      setResettingSlug(null);
      window.setTimeout(() => setFlash(null), 4200);
    }
  };

  const counts = useMemo(() => {
    let custom = 0, hero = 0, def = 0;
    rows.forEach((r) => {
      if (r.state === "custom") custom++;
      else if (r.state === "hero") hero++;
      else def++;
    });
    return { total: rows.length, custom, hero, default: def };
  }, [rows]);

  return (
    <section className="bg-[#FDFBF7] min-h-screen" data-testid="admin-widget-covers-page">
      <header className="px-6 md:px-10 py-8 border-b border-[#2C2621]/10 bg-white">
        <div className="max-w-[1600px] mx-auto">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542] transition-colors"
            data-testid="admin-widget-covers-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to admin hub
          </Link>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-3xl">
              <p className="overline text-[#C16542]">Booking widget · Photo direction</p>
              <h1 className="font-serif-x text-3xl md:text-4xl tracking-tight leading-tight mt-2">
                Widget covers
              </h1>
              <p className="mt-3 text-sm text-[#5C5248] leading-relaxed">
                Curate the property photos guests see across every instance of the booking
                widget — the visual carousel, the &ldquo;Choose a property&rdquo; dropdown, the
                per-property blocks, the itinerary strip and the floating enquiry summary.
                One edit here propagates to every surface, on every page the widget
                appears.
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 min-w-[260px]" data-testid="admin-widget-covers-stats">
              <div className="text-center py-2 bg-[#C16542]/10 border border-[#C16542]/30" data-testid="admin-widget-covers-stat-custom">
                <div className="text-2xl font-serif-x tabular-nums text-[#C16542]">{counts.custom}</div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-[#C16542]">Custom</div>
              </div>
              <div className="text-center py-2 bg-[#2C2621]/8 border border-[#2C2621]/25" data-testid="admin-widget-covers-stat-hero">
                <div className="text-2xl font-serif-x tabular-nums text-[#2C2621]">{counts.hero}</div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-[#5C5248]">Hotel hero</div>
              </div>
              <div className="text-center py-2 bg-[#E8DBC4]/50 border border-[#5C5248]/25" data-testid="admin-widget-covers-stat-default">
                <div className="text-2xl font-serif-x tabular-nums text-[#5C5248]">{counts.default}</div>
                <div className="text-[9px] tracking-[0.25em] uppercase text-[#5C5248]">Fallback</div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Flash message */}
      {flash ? (
        <div
          className={`sticky top-0 z-30 border-b ${flash.type === "ok" ? "bg-[#22513E]/95 border-[#22513E] text-[#FDFBF7]" : "bg-[#8A2E2E]/95 border-[#8A2E2E] text-[#FDFBF7]"}`}
          data-testid="admin-widget-covers-flash"
        >
          <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-2.5 flex items-center gap-2 text-xs">
            {flash.type === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
            <span>{flash.msg}</span>
          </div>
        </div>
      ) : null}

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-8">
        {loadError ? (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 text-sm flex items-center gap-2" data-testid="admin-widget-covers-error">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{loadError}</span>
          </div>
        ) : null}

        {/* Grid */}
        <div className="flex items-center gap-2 mb-4 text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
          <LayoutGrid className="w-3.5 h-3.5 text-[#C16542]" />
          {loading ? "Loading covers…" : `${rows.length} properties`}
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
          data-testid="admin-widget-covers-grid"
        >
          {rows.map((row) => {
            const stateMeta = STATE_META[row.state];
            return (
              <article
                key={row.slug}
                className="bg-white border border-[#2C2621]/10 flex flex-col"
                data-testid={`admin-widget-cover-${row.slug}`}
              >
                <div className="relative aspect-[4/3] bg-[#1A1513]/5 overflow-hidden">
                  {row.displayUrl ? (
                    <img
                      src={row.displayUrl}
                      alt={row.name}
                      loading="lazy"
                      className="w-full h-full object-cover"
                      data-testid={`admin-widget-cover-${row.slug}-thumb`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#5C5248] text-xs">
                      No image
                    </div>
                  )}
                  <span
                    className={`absolute top-2 left-2 inline-flex items-center px-2 py-1 text-[9px] tracking-[0.22em] uppercase ${stateMeta.className}`}
                    data-testid={`admin-widget-cover-${row.slug}-state`}
                  >
                    {stateMeta.label}
                  </span>
                  <span className="absolute top-2 right-2 inline-flex items-center px-2 py-1 text-[9px] tracking-[0.22em] uppercase bg-[#FDFBF7]/95 text-[#5C5248] border border-[#2C2621]/10">
                    {CATEGORY_LABELS[row.category] || row.category}
                  </span>
                </div>

                <div className="p-3 flex flex-col flex-1">
                  <p className="font-serif-x text-base text-[#2C2621] leading-tight truncate" title={row.name}>
                    {row.name}
                  </p>
                  {row.location ? (
                    <p className="mt-0.5 text-[10px] tracking-[0.18em] uppercase text-[#5C5248] truncate">
                      {row.location}
                    </p>
                  ) : null}

                  <div className="mt-3 flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => handleEdit(row)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-2 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] text-[10px] tracking-[0.22em] uppercase transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
                      data-testid={`admin-widget-cover-${row.slug}-edit`}
                    >
                      {row.state === "custom" ? <Pencil className="w-3 h-3" /> : <ImagePlus className="w-3 h-3" />}
                      {row.state === "custom" ? "Edit" : "Set cover"}
                    </button>
                    {row.state === "custom" ? (
                      <button
                        type="button"
                        onClick={() => handleReset(row)}
                        disabled={resettingSlug === row.slug}
                        className="inline-flex items-center justify-center px-2 py-2 border border-[#2C2621]/25 hover:border-[#C16542] hover:text-[#C16542] text-[#5C5248] transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A373]"
                        aria-label={`Reset ${row.name} to hotel hero`}
                        title="Reset to hotel hero"
                        data-testid={`admin-widget-cover-${row.slug}-reset`}
                      >
                        {resettingSlug === row.slug ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <RotateCcw className="w-3.5 h-3.5" />
                        )}
                      </button>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {/* Live preview */}
        <div className="mt-14 pt-10 border-t border-[#2C2621]/10">
          <div className="flex items-center gap-2 mb-4 text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">
            <Eye className="w-3.5 h-3.5 text-[#C16542]" />
            Live preview · Exactly what guests see
          </div>
          <div className="bg-white border border-[#2C2621]/10 p-4 md:p-6" data-testid="admin-widget-covers-preview">
            <PropertyCarousel
              selectedSlugs={[]}
              onToggle={() => {}}
              lang={lang}
            />
          </div>
        </div>
      </div>

      {editing ? (
        <ImageEditModal
          slot={editing.slot}
          currentUrl={editing.currentUrl}
          currentAlt={editing.currentAlt}
          currentAltI18n={editing.currentAltI18n}
          onClose={handleEditClose}
        />
      ) : null}
    </section>
  );
};

const AdminWidgetCoversPage = () => (
  <AdminAuthGate>
    <AdminWidgetCoversInner />
  </AdminAuthGate>
);

export default AdminWidgetCoversPage;
